import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import ResourceDrawer from "@/components/common/ResourceDrawer";
import { useNavigate } from "react-router-dom";

const TYPE_GROUPS = [
    { key: "asset", label: "Assets" }, { key: "liability", label: "Liabilities" },
    { key: "equity", label: "Equity" }, { key: "income", label: "Income" },
    { key: "cogs", label: "Cost of Goods Sold" }, { key: "expense", label: "Expenses" },
    { key: "other_income", label: "Other Income" }, { key: "other_expense", label: "Other Expenses" },
];

export default function ChartOfAccounts() {
    const [items, setItems] = useState([]);
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const navigate = useNavigate();

    const load = async () => {
        const { data } = await api.get("/coa"); setItems(data);
    };
    useEffect(() => { load(); }, []);

    const fields = [
        { name: "code", label: "Account code" }, { name: "name", label: "Account name" },
        { name: "type", label: "Type", type: "select", options: TYPE_GROUPS.map((g) => ({ value: g.key, label: g.label })) },
        { name: "sub_type", label: "Sub-type" },
        { name: "opening_balance", label: "Opening balance", type: "number" },
        { name: "description", label: "Description", type: "textarea", span: 2 },
    ];

    const save = async (form) => {
        if (drawer.item) await api.put(`/coa/${drawer.item.id}`, form);
        else await api.post(`/coa`, form);
        await load();
    };

    return (
        <>
            <PageHeader kicker="ACCOUNTING" title="Chart of Accounts" subtitle="The blueprint of your books — every account, every type."
                actions={<Button onClick={() => setDrawer({ open: true, item: null })} data-testid="coa-new"><Plus className="w-4 h-4 mr-1.5" />New account</Button>} />
            <PageBody>
                <div className="space-y-4">
                    {TYPE_GROUPS.map((g) => {
                        const rows = items.filter((i) => i.type === g.key);
                        if (rows.length === 0) return null;
                        const total = rows.reduce((s, r) => s + (r.current_balance || 0), 0);
                        return (
                            <div key={g.key} className="bg-card border border-border rounded-md overflow-hidden">
                                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{g.label}</div>
                                        <div className="text-[12px] text-muted-foreground/80">{rows.length} accounts</div>
                                    </div>
                                    <div className="serif-num text-xl">{fmtMoney(total)}</div>
                                </div>
                                <table className="fin-table">
                                    <thead><tr><th>Code</th><th>Name</th><th>Sub-type</th><th className="num">Balance</th></tr></thead>
                                    <tbody>
                                        {rows.map((a) => (
                                            <tr key={a.id} className="cursor-pointer" onClick={() => navigate(`/accounting/ledger?account=${a.id}`)} data-testid={`coa-row-${a.id}`}>
                                                <td className="font-mono text-[12px]">{a.code}</td>
                                                <td className="font-medium">{a.name}</td>
                                                <td className="text-[12px] text-muted-foreground">{a.sub_type}</td>
                                                <td className="num">{fmtMoney(a.current_balance || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })}
                </div>
            </PageBody>
            <ResourceDrawer open={drawer.open} onClose={() => setDrawer({ open: false, item: null })} title={drawer.item ? "Edit account" : "New account"} fields={fields} initial={drawer.item || { type: "expense" }} onSave={save} />
        </>
    );
}
